import React, { useCallback, useMemo, useState } from 'react'
import { StyleSheet, View, Pressable } from 'react-native'
import { List, Text } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { useNavigation } from '@react-navigation/native'
import { FlatList, RefreshControl } from 'react-native-gesture-handler'
import {
  TimeLineAction,
  TimeLine,
  useAvailableNodesQuery,
} from '../../../../generated/graphql'
import { AppNavigation } from '../../../../navigation/types'
import { UserLink } from '../../../../@ui/typo/user.link'
import { TimeLineIcon } from '../../../../utils/meldd-icons'
import { ReplyActionTitle } from '../../../core/replay/replay.screen'
import { EmptyList } from '../../../../@ui/containers/lists/empty-list'
import { HoverableItem } from '../../../../@ui/buttons/hooverable.area'
import Dropdown from '../../../../@ui/form/dropdown.input'
import Row from '../../../../@ui/containers/grid/row'
import { navigateToTarget } from '../../../../utils/target'
import { timelineActionsAvailableForFilters } from '../timeline-excluded-event-types'
import { NewIndicatorDot } from '../../../../@ui/badges'

export type TimelineResultItem = TimeLine

/** Minimal timeline item for compact display (e.g. last event on collaborator row). */
export type TimelineCompactItem = Pick<
  TimeLine,
  'id' | 'title' | 'timestamp' | 'action' | 'targetType' | 'targetId'
> & {
  nodeName?: string | null
  payload?: { type?: string | null } | null
}

export const TimelineCompactRow = ({
  item,
  style,
  onPress,
}: {
  item: TimelineCompactItem
  style?: import('react-native').StyleProp<import('react-native').ViewStyle>
  onPress?: () => void
}) => {
  const timestamp = useMemo(
    () => (item?.timestamp ? new Date(item.timestamp).toLocaleString() : ''),
    [item?.timestamp]
  )
  const titleText =
    item.payload?.type != null ? (
      <ReplyActionTitle item={item as TimeLine} />
    ) : (
      item.title
    )

  const content = (
    <View style={styles.compactRow}>
      <View style={styles.leftIconContainer}>
        <TimeLineIcon item={item as TimeLine} size={14} />
      </View>
      <Text
        variant="labelSmall"
        style={styles.compactRowTitle}
        numberOfLines={1}
      >
        {titleText}
        {item.nodeName ? ` · ${item.nodeName}` : ''}
      </Text>
      <Text variant="bodySmall" style={styles.compactRowTime}>
        {timestamp}
      </Text>
    </View>
  )

  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        style={({ pressed }) => [style, pressed && styles.compactRowPressed]}
      >
        {content}
      </Pressable>
    )
  }

  return <View style={style}>{content}</View>
}

const PayloadDescription = ({ item }: { item: TimeLine }) => {
  if (item.payload.originalText) {
    return (
      <Text variant="bodySmall" style={{ fontStyle: 'italic' }}>
        "{item.payload.originalText}" → "{item.payload.title}"
      </Text>
    )
  }
  return (
    <Text variant="bodySmall" style={{ fontStyle: 'italic' }}>
      {item.payload.title}
    </Text>
  )
}

const TimelineListItem = ({
  item,
  onPress,
  anonymousLabel,
  showNode = false,
}: {
  item: TimeLine
  onPress: (item: TimeLine) => void
  anonymousLabel: string
  showNode?: boolean
}) => {
  const { t } = useTranslation('nodes')
  const [hover, setHover] = useState(false)
  const timestamp = useMemo(
    () => (item?.timestamp ? new Date(item.timestamp).toLocaleString() : ''),
    [item?.timestamp]
  )

  if (!item) {
    return null
  }

  return (
    <HoverableItem onHover={(isHovered) => setHover(isHovered)}>
      <List.Item
        style={[
          styles.listItem,
          {
            backgroundColor: hover
              ? 'rgba(100, 149, 237, 0.25)'
              : 'transparent',
          },
        ]}
        title={
          <Row alignItems="center" columnGap={2}>
            <NewIndicatorDot
              visible={item.isNew}
              accessibilityLabel={t('new-collaborator-timeline-item')}
            />
            <Text variant="labelSmall" style={{ flex: 1 }}>
              <ReplyActionTitle item={item} />
            </Text>
          </Row>
        }
        left={() => (
          <View style={styles.leftIconContainer}>
            <TimeLineIcon item={item} size={14} />
          </View>
        )}
        right={() => (
          <View style={{ flexDirection: 'column', alignItems: 'flex-end' }}>
            {item.user ? (
              <UserLink user={item.user} />
            ) : (
              <Text variant="bodySmall">{anonymousLabel}</Text>
            )}
            <Text variant="bodySmall" style={{ marginLeft: 8, paddingTop: 8 }}>
              {timestamp}
            </Text>
            {showNode && item.nodeName && (
              <Text
                variant="bodySmall"
                style={{ marginLeft: 8, paddingTop: 4, opacity: 0.7 }}
              >
                {item.nodeName}
              </Text>
            )}
          </View>
        )}
        description={
          <View style={{ paddingTop: 8 }}>
            <PayloadDescription item={item} />
          </View>
        }
        onPress={() => onPress(item)}
      />
    </HoverableItem>
  )
}

export type TimelineListProps = {
  results: TimelineResultItem[]
  total: number
  loading: boolean
  onRefresh: () => Promise<void>
  onLoadMore: () => void
  emptyMessage: string
  showUserFilter?: boolean
  userFilterItems?: Array<{ id: string; label: string }>
  selectedUserId?: string | null
  onUserFilterSelect?: (item: { id: string | number; label: string }) => void
  onUserFilterSearch?: (query: string) => void
  selectedAction?: TimeLineAction | null
  onActionFilterSelect?: (action: TimeLineAction | null) => void
  showNode?: boolean
  selectedNodeId?: string | null
  onNodeFilterSelect?: (nodeId: string | null) => void
  /** When true (collaborators updates tab), show blue dots from GraphQL `item.isNew` (server `timelineReadUntil`). */
  collaboratorsUnreadMarkers?: boolean
}

export const TimelineList: React.FC<TimelineListProps> = ({
  results,
  total,
  loading,
  onRefresh,
  onLoadMore,
  emptyMessage,
  showUserFilter = false,
  userFilterItems = [],
  selectedUserId,
  onUserFilterSelect,
  onUserFilterSearch,
  selectedAction,
  onActionFilterSelect,
  showNode = false,
  selectedNodeId,
  onNodeFilterSelect,
  collaboratorsUnreadMarkers = false,
}) => {
  const { t } = useTranslation(['nodes', 'common', 'eagle-view'])
  const navigation = useNavigation<AppNavigation>()
  const anonymousLabel = t('common:anonymous', 'Anonymous')

  const { data: nodesData } = useAvailableNodesQuery()
  const nodes = nodesData?.availableNodes ?? []
  const nodeItems = useMemo(() => {
    const allOption = { id: '', label: t('nodes:all') }
    const list = nodes.map((node) => ({
      id: node.id,
      label: node.name,
    }))
    return [allOption, ...list].sort((a, b) => a.label.localeCompare(b.label))
  }, [nodes, t])

  const eventTypeItems = useMemo(() => {
    const allOption = { id: '', label: t('nodes:all') }
    /** Excluded list: `TIMELINE_EXCLUDED_EVENT_TYPES` — dropdown only; callers must pass matching `actions` on the query to hide rows. */
    const list = timelineActionsAvailableForFilters().map((action) => {
      if (
        [
          TimeLineAction.ContributionApproved,
          TimeLineAction.ContributionRejected,
        ].includes(action)
      ) {
        return {
          id: action,
          label: t(`eagle-view:action.${action}`, {}).replace(
            '{{type}}',
            t('tasks:contribution')
          ),
        }
      }
      return { id: action, label: t(`eagle-view:action.${action}`) }
    })
    return [allOption, ...list].sort((a, b) => a.label.localeCompare(b.label))
  }, [t])

  const selectedEventType =
    selectedAction != null
      ? eventTypeItems.find((item) => item.id === selectedAction)
      : undefined

  const handleEventTypeSelect = useCallback(
    (item: { id: string | number; label: string }) => {
      const action = item.id === '' ? null : (item.id as TimeLineAction)
      onActionFilterSelect?.(action)
    },
    [onActionFilterSelect]
  )

  const selectedUser =
    selectedUserId != null
      ? userFilterItems.find((item) => item.id === selectedUserId)
      : undefined

  const selectedNode =
    selectedNodeId != null
      ? nodeItems.find((item) => item.id === selectedNodeId)
      : undefined

  const handleNodeSelect = useCallback(
    (item: { id: string | number; label: string }) => {
      const nodeId = item.id === '' ? null : (item.id as string)
      onNodeFilterSelect?.(nodeId)
    },
    [onNodeFilterSelect]
  )

  const handleItemPress = useCallback(
    (item: TimeLine) => {
      navigateToTarget({
        target: { type: item.targetType, id: item.targetId },
        navigation,
      })
    },
    [navigation]
  )

  const showCollaboratorNewDots = collaboratorsUnreadMarkers === true

  return (
    <View style={styles.container}>
      <Row alignItems="center" wrap columnGap={8} style={styles.filtersRow}>
        {showUserFilter && onUserFilterSelect && (
          <Dropdown<{ id: string; label: string }>
            items={userFilterItems}
            selected={selectedUser}
            onSelect={
              onUserFilterSelect as (item: {
                id: string
                label: string
              }) => void
            }
            placeholder={t('nodes:filter-by-collaborator')}
            searchPlaceholder={t('nodes:search-collaborators')}
            icon="account-outline"
            showSearch
            useLocalFilter={false}
            onSearch={onUserFilterSearch}
          />
        )}
        {onActionFilterSelect && (
          <Dropdown<{ id: string; label: string }>
            items={eventTypeItems}
            selected={selectedEventType}
            onSelect={handleEventTypeSelect}
            placeholder={t('nodes:filter-by-event-type')}
            searchPlaceholder={t('common:search-placeholder', 'Search...')}
            icon="filter-outline"
            showSearch
            useLocalFilter
          />
        )}
        {onNodeFilterSelect && (
          <Dropdown<{ id: string; label: string }>
            items={nodeItems}
            selected={selectedNode}
            onSelect={handleNodeSelect}
            placeholder={t('nodes:filter-by-node', 'Node')}
            searchPlaceholder={t('nodes:search-nodes', 'Search nodes...')}
            icon="domain"
            showSearch
            useLocalFilter
          />
        )}
      </Row>
      {!loading && results.length === 0 ? (
        <View style={styles.empty}>
          <EmptyList message={emptyMessage} />
        </View>
      ) : (
        <FlatList
          data={results}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <TimelineListItem
              item={item}
              onPress={handleItemPress}
              anonymousLabel={anonymousLabel}
              showNode={showNode}
            />
          )}
          refreshControl={
            <RefreshControl refreshing={loading} onRefresh={onRefresh} />
          }
          onEndReached={onLoadMore}
          onEndReachedThreshold={0.3}
        />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  filtersRow: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  empty: {
    flex: 1,
    justifyContent: 'center',
  },
  listItem: {
    paddingHorizontal: 4,
    paddingBottom: 0,
    paddingTop: 0,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e0e0e0',
  },
  leftIconContainer: {
    alignSelf: 'stretch',
    justifyContent: 'center',
  },
  compactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
    columnGap: 8,
  },
  compactRowTitle: {
    flex: 1,
  },
  compactRowTime: {
    opacity: 0.7,
  },
  compactRowPressed: {
    opacity: 0.7,
  },
})
